import Grid from '@mui/material/Grid';

import type { HighlightIconText } from '@/components/HighlightMediaText/HighlightMediaText';
import TextContent, {
  type TextContentAppLinks,
} from '@/components/HighlightMediaText/TextContent/TextContent';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import VimeoVideo, {
  type VimeoVideoType,
} from '@/components/VimeoVideo/VimeoVideo';
import {
  ContentStyle,
  type TextAlignment,
} from '@/contentful/contentful.types';
import type {
  Contentful,
  FeaturedComponent,
  FrontifyImageContent,
} from '@/contentful/contentful-exports';
import {
  getFrontifyBackgroundImageUrl,
  MAX_IMAGE_WIDTH,
} from '@/utils/frontify';

import useStyles from './HeroImageTextFiftyFifty.styles';

export interface HeroImageTextFiftyFiftyContent extends Contentful {
  fields: {
    id: string;
    style?: ContentStyle;
    textAlignment?: TextAlignment.Left | TextAlignment.Center;
    eyebrow?: string;
    headline?: string;
    subhead?: string;
    video?: VimeoVideoType;
    images?: FrontifyImageContent[];
    imageAltText?: string;
    imageOnTopInMobile?: boolean;
    features?: HighlightIconText[];
    cta?: ButtonContent[];
    appLinks?: TextContentAppLinks;
  };
}

type HeroImageTextFiftyFiftyProps = FeaturedComponent<{
  content: HeroImageTextFiftyFiftyContent;
}>;

export default function HighlightText({
  isFeatured,
  content: {
    fields: {
      style,
      textAlignment,
      eyebrow,
      headline,
      subhead,
      features,
      cta,
      video,
      imageAltText,
      imageOnTopInMobile,
      images,
      appLinks,
      ...props
    },
  },
}: HeroImageTextFiftyFiftyProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });

  const isLeftAligned = textAlignment === 'Left';
  const videoFields = video?.fields;

  return (
    <SpacingContainer className={classes.container} isFeatured={isFeatured}>
      <GridContainer
        gridProps={{
          className: cx(
            classes.wrapper,
            imageOnTopInMobile && 'reverseColumn',
            isLeftAligned && 'reverseRow'
          ),
        }}
      >
        <TextContent
          subhead={subhead}
          subheadClassName={classes.subhead}
          features={features}
          cta={cta}
          appLinks={appLinks}
          isHeroMode={true}
          isFixed={false}
          eyebrow={eyebrow}
          headline={headline}
          isOnDark={isOnDark}
          gridSizes={{ lg: 6, md: 6, sm: 12, xs: 12 }}
          contentClassName={classes.textContent}
          {...props}
        />
        <Grid
          className={classes.imageContainer}
          size={{
            lg: 6,
            md: 6,
            sm: 12,
            xs: 12,
          }}
        >
          {video && videoFields?.videoUrl ? (
            <VimeoVideo
              url={videoFields.videoUrl}
              autoplay={videoFields?.autoPlay}
              controls={!videoFields?.autoPlay}
              isRounded
            />
          ) : (
            <div
              className={classes.image}
              role='img'
              aria-label={imageAltText}
              style={{
                backgroundImage: images
                  ? getFrontifyBackgroundImageUrl(images[0], MAX_IMAGE_WIDTH)
                  : '',
              }}
            />
          )}
        </Grid>
      </GridContainer>
    </SpacingContainer>
  );
}
